import { pcToast } from './pc/pc-ui';
import {
  getSanGaoStaffInfo,
  getSanGaoStaffReadableOrganizations,
  getSanGaoStaffSalesmanList,
  getSanGaoStaffCustomerList,
  switchSanGaoStaffOrg,
  auditSanGaoStaffCustomer,
  deleteSanGaoStaffPendingQuestionnaire,
} from '@/api/sanGaoStaff';
import { saveStaffSession } from './staff-session';
import { isTaikangStaffMode } from '@/utils/sanGaoStaffMode';
import ScreeningReviewNotice from './screening-review-notice.vue';
import { isScreeningPassed, screeningStatus, SCREENING_MEDICAL_CONFIRMATION } from './pc/customer-data';
import { AUDIT_OPTIONS, EXAM_OPTIONS, ASSESSMENT_OPTIONS, DISEASE_OPTIONS, hasValue, statusInfo, diseaseText, qualifiedText, qualifiedInfo, questionnaireText, examInfo, maskIdCard, formatTime, reservationText, downloadCustomerCsv } from './pc/customer-data';

const STORAGE_KEYS = { token: 'san_gao_staff_token', info: 'san_gao_staff_info' };
const emptyFilters = () => ({
  orgIds: null,
  keyword: '',
  salesmanId: '',
  disease: '',
  qualified: '',
  exam: '',
  assessment: '',
  from: '',
  to: '',
});
export default {
  name: 'SanGaoStaffWorkbench',
  components: { ScreeningReviewNotice },
  data() {
    return {
      staffName: '', staffPhone: '', orgId: null, orgName: '', organizations: [], autoAudit: false, staffTaikangMode: false, customerList: [], salesmanList: [],
      showOrgPicker: false, selectedOrgId: null, switchingOrg: false, infoRequestId: 0, salesmanRequestId: 0,
      readableOrganizations: [],
      organizationRequestId: 0,
      draftFilters: emptyFilters(), filters: emptyFilters(), activeTab: '', sortDirection: 'desc',
      pageSize: 20, currentPage: 1, pageJump: 1, selectedIds: [],
      loading: false, dataReady: false, loadError: '', lastUpdated: null, requestId: 0,
      auditing: false, auditTargets: [], auditTargetStatus: 1, showAuditConfirm: false,
      showDeleteConfirm: false, deleteTarget: null,
      showExportConfirm: false, exporting: false,
      drawerRow: null, drawerTab: 'overview',
      tabs: [{ value: '', label: '全部客户' }, ...AUDIT_OPTIONS],
      auditOptions: AUDIT_OPTIONS, examOptions: EXAM_OPTIONS,
      assessmentOptions: ASSESSMENT_OPTIONS, diseaseOptions: DISEASE_OPTIONS,
    };
  },
  computed: {
    organizationFilterEnabled() {
      return this.$options.enableOrganizationFilter === true;
    },
    hasPeerOrganizationOptions() {
      return this.orgId != null && this.readableOrganizations.some(
        organization => String(organization.orgId) !== String(this.orgId)
      );
    },
    draftOrganizationIds: {
      get() {
        const visibleIds = new Set(this.readableOrganizations.map(organization => String(organization.orgId)));
        const selectedIds = this.draftFilters.orgIds || [String(this.orgId)];
        // 本机构可能没有客户，仍可默认查询，但不能作为隐藏的勾选项发出去。
        return selectedIds.filter(id => visibleIds.has(id));
      },
      set(ids) {
        this.draftFilters.orgIds = ids;
        // 机构范围变更后，旧的业务员选项可能不再适用。
        this.draftFilters.salesmanId = '';
      },
    },
    draftOrganizationLabel() {
      const ids = this.draftOrganizationIds;
      if (ids.length === 1) {
        const option = this.readableOrganizations.find(item => String(item.orgId) === ids[0]);
        return option ? option.orgName : '本机构';
      }
      return ids.length ? `已选 ${ids.length} 个机构` : '未选择（默认本机构）';
    },
    taikangMode() { return isTaikangStaffMode(this.$route.query) || this.staffTaikangMode; },
    extendedDataReady() { return this.customerList.every(row => hasValue(row.submitId)); },
    maskedPhone() { return this.staffPhone.length >= 7 ? `${this.staffPhone.slice(0, 3)}****${this.staffPhone.slice(-4)}` : this.staffPhone || '—'; },
    summary() {
      return this.customerList.reduce((result, row) => {
        if (String(row.auditStatus) === '0') result.pending++;
        if (screeningStatus(row) === 'REJECTED') result.unqualified++;
        if (screeningStatus(row) === 'PENDING_CONFIRMATION') result.pendingConfirmation++;
        if (String(row.examStatus) === '1') result.booked++;
        if (String(row.examStatus) === '4') result.reported++;
        if (String(row.examStatus) === '5') result.transmitted++;
        return result;
      }, { pending: 0, unqualified: 0, pendingConfirmation: 0, booked: 0, reported: 0, transmitted: 0 });
    },
    filtersDirty() { return JSON.stringify(this.draftFilters) !== JSON.stringify(this.filters); },
    appliedFilterCount() { return Object.values(this.filters).filter(Boolean).length + (this.activeTab !== '' ? 1 : 0); },
    baseRows() {
      const f = this.filters, keyword = f.keyword.toLowerCase();
      const matches = (filter, value) => !filter || (filter === 'NONE' ? !hasValue(value) : String(value) === filter);
      return this.customerList.filter(row => {
        if (keyword && ![row.personName, row.phone, row.idCard, row.salesmanName, row.customerId].some(value => String(value || '').toLowerCase().includes(keyword))) return false;
        if (f.salesmanId && String(row.salesmanId) !== f.salesmanId) return false;
        if (f.disease && row.disease !== f.disease) return false;
        if (!matches(f.qualified, screeningStatus(row)) || !matches(f.exam, row.examStatus) || !matches(f.assessment, row.assessmentStatus)) return false;
        const date = formatTime(row.submitTime).slice(0, 10);
        if ((f.from || f.to) && !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
        return !(f.from && date < f.from || f.to && date > f.to);
      });
    },
    filteredRows() {
      const rows = this.baseRows.filter(row => this.activeTab === '' || String(row.auditStatus) === this.activeTab);
      return rows.sort((a, b) => {
        const aTime = formatTime(a.submitTime), bTime = formatTime(b.submitTime);
        if (aTime === '—' && bTime !== '—') return 1;
        if (bTime === '—' && aTime !== '—') return -1;
        const order = aTime.localeCompare(bTime) || String(a.customerId).localeCompare(String(b.customerId), undefined, { numeric: true });
        return this.sortDirection === 'desc' ? -order : order;
      });
    },
    pageCount() { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); },
    pageRows() { return this.filteredRows.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize); },
    selectablePageRows() { return this.pageRows.filter(this.isAuditEligible); },
    selectedRows() { const ids = new Set(this.selectedIds); return this.filteredRows.filter(row => this.isAuditEligible(row) && ids.has(row.customerId)); },
    allPageSelected() { return this.selectablePageRows.length > 0 && this.selectablePageRows.every(row => this.selectedIds.includes(row.customerId)); },
    somePageSelected() { return this.selectablePageRows.some(row => this.selectedIds.includes(row.customerId)); },
    canBatchAudit() { return !this.loading && !this.auditing && this.selectedRows.length > 0 && this.selectedRows.length <= 100 && this.selectedRows.every(row => String(row.auditStatus) === '0'); },
    hasSelectedUnsuitable() { return this.selectedRows.some(row => !isScreeningPassed(row)); },
    canBatchApprove() { return this.canBatchAudit && !this.hasSelectedUnsuitable; },
    canExport() { return this.dataReady && !this.loading && !this.loadError && !this.exporting && !this.auditing && this.filteredRows.length > 0; },
    exportRows() { return this.filteredRows; },
    auditNames() {
      return this.customerList
        .filter(row => this.auditTargets.includes(row.customerId) && this.isAuditEligible(row))
        .map(row => row.personName || `客户 ${row.customerId}`).join('、');
    },
  },
  created() {
    this.loadStaffInfo();
    this.loadReadableOrganizations();
    this.loadSalesmanList();
    this.loadCurrent();
  },
  beforeDestroy() {
    this.requestId++;
    this.infoRequestId++;
    this.salesmanRequestId++;
    this.organizationRequestId++;
  },
  methods: {
    notify(message) { pcToast(message); },
    statusInfo, diseaseText, qualifiedText, qualifiedInfo, questionnaireText, examInfo, maskIdCard, formatTime, reservationText,
    metric(value) { return this.dataReady && !this.loading ? value : '—'; },
    tabCount(value) { return value === '' ? this.baseRows.length : this.baseRows.filter(row => String(row.auditStatus) === value).length; },
    salesmanCode(id) { const salesman = this.salesmanList.find(item => String(item.id) === String(id)); return salesman && salesman.salesmanCode ? `编号 ${salesman.salesmanCode}` : ''; },
    organizationParams() {
      const ids = this.filters.orgIds;
      return this.organizationFilterEnabled && ids && ids.length
        ? { orgIds: ids.join(',') }
        : {};
    },
    async loadReadableOrganizations() {
      if (!this.organizationFilterEnabled) return;
      const requestId = ++this.organizationRequestId;
      try {
        const res = await getSanGaoStaffReadableOrganizations();
        if (requestId !== this.organizationRequestId) return;
        if (!res || res.code !== 200 || !Array.isArray(res.data)) {
          throw new Error('机构选项加载失败');
        }
        this.readableOrganizations = res.data;
      } catch (error) {
        if (requestId === this.organizationRequestId) {
          this.notify('机构选项加载失败，仍可查看本机构数据');
        }
      }
    },
    async loadStaffInfo() {
      const requestId = ++this.infoRequestId;
      try { const cached = JSON.parse(localStorage.getItem(STORAGE_KEYS.info) || 'null'); if (cached) this.fillInfo(cached); } catch (e) { /* 缓存损坏时仍请求服务端 */ }
      try { const res = await getSanGaoStaffInfo(); if (requestId !== this.infoRequestId) return; if (res && res.code === 200 && res.data) { this.fillInfo(res.data); localStorage.setItem(STORAGE_KEYS.info, JSON.stringify({ ...res.data, orgName: this.orgName })); } }
      catch (e) { /* 登录失效由请求拦截器处理 */ }
    },
    fillInfo(info) { this.staffName = info.personName || ''; this.staffPhone = info.personPhone || ''; this.orgId = info.orgId; this.orgName = info.orgName || ''; this.organizations = Array.isArray(info.organizations) ? info.organizations : []; this.autoAudit = info.autoAudit === true; this.staffTaikangMode = info.taikangMode === true; },
    openOrgPicker() { if (this.taikangMode || this.organizations.length < 2 || this.switchingOrg || this.auditing || this.exporting || this.loading) return; this.selectedOrgId = this.orgId; this.showOrgPicker = true; },
    async confirmOrgSwitch() {
      if (this.taikangMode || !this.selectedOrgId || this.switchingOrg || this.auditing || this.exporting) return;
      if (String(this.selectedOrgId) === String(this.orgId)) { this.showOrgPicker = false; return; }
      this.switchingOrg = true;
      try {
        const res = await switchSanGaoStaffOrg(this.selectedOrgId);
        if (!res || res.code !== 200 || !res.data) throw new Error((res && res.msg) || '切换机构失败');
        this.requestId++; this.infoRequestId++; this.salesmanRequestId++; this.organizationRequestId++;
        saveStaffSession(res.data); this.fillInfo(res.data);
        this.showOrgPicker = false; this.selectedIds = []; this.drawerRow = null;
        this.showAuditConfirm = false; this.showDeleteConfirm = false; this.showExportConfirm = false;
        if (this.showFilters !== undefined) this.showFilters = false;
        this.customerList = []; this.salesmanList = []; this.dataReady = false;
        this.readableOrganizations = [];
        this.draftFilters = emptyFilters(); this.filters = emptyFilters(); this.activeTab = ''; this.currentPage = 1; this.pageJump = 1;
        await Promise.all([
          this.loadReadableOrganizations(),
          this.loadSalesmanList(),
          this.loadCurrent(),
        ]);
        this.notify(`已切换至${this.orgName || '所选机构'}`);
      } catch (error) { this.notify(error.message || '切换机构失败，请重试'); }
      finally { this.switchingOrg = false; }
    },
    async loadSalesmanList() {
      const requestId = ++this.salesmanRequestId;
      try {
        const res = await getSanGaoStaffSalesmanList(this.organizationParams());
        if (requestId !== this.salesmanRequestId) return;
        if (res && res.code === 200 && Array.isArray(res.data)) {
          this.salesmanList = res.data;
        } else {
          this.notify('业务员筛选项加载失败，可刷新页面重试');
        }
      }
      catch (e) { if (requestId === this.salesmanRequestId) this.notify('业务员筛选项加载失败，可刷新页面重试'); }
    },
    async loadCurrent() {
      const requestId = ++this.requestId;
      this.loading = true; this.loadError = ''; this.selectedIds = []; this.drawerRow = null;
      try {
        // 不传机构时默认本机构；所选机构必须由服务端核验访问权限。
        const res = await getSanGaoStaffCustomerList(this.organizationParams());
        if (requestId !== this.requestId) return;
        if (!res || res.code !== 200 || !Array.isArray(res.data)) throw new Error((res && res.msg) || '客户列表返回异常');
        this.customerList = res.data; this.dataReady = true; this.lastUpdated = new Date();
        this.currentPage = Math.min(this.currentPage, this.pageCount); this.pageJump = this.currentPage;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.customerList = []; this.dataReady = false; this.lastUpdated = null; this.loadError = error.message || '网络异常，请稍后重试';
      } finally { if (requestId === this.requestId) this.loading = false; }
    },
    refreshWorkbench() {
      return Promise.all([this.loadReadableOrganizations(), this.loadCurrent()]);
    },
    applyFilters() {
      if (this.auditing || this.loading || this.switchingOrg) return;
      if (this.draftFilters.from && this.draftFilters.to && this.draftFilters.from > this.draftFilters.to) { this.notify('开始日期不能晚于结束日期'); return; }
      const ids = this.draftOrganizationIds;
      const orgIds = ids && ids.length && !(ids.length === 1 && ids[0] === String(this.orgId))
        ? [...new Set(ids)].sort()
        : null;
      this.draftFilters.orgIds = orgIds;
      const scopeChanged = JSON.stringify(this.filters.orgIds) !== JSON.stringify(orgIds);
      this.filters = { ...this.draftFilters };
      this.selectedIds = [];
      this.changePage(1);
      if (scopeChanged) this.reloadOrganizationScope();
    },
    resetFilters() {
      if (this.auditing || this.loading || this.switchingOrg) return;
      const scopeChanged = !!this.filters.orgIds;
      this.draftFilters = emptyFilters();
      this.filters = emptyFilters();
      this.activeTab = '';
      this.selectedIds = [];
      this.changePage(1);
      if (scopeChanged) this.reloadOrganizationScope();
    },
    reloadOrganizationScope() {
      this.salesmanList = [];
      this.loadSalesmanList();
      this.loadCurrent();
    },
    setTab(value) { this.activeTab = value; this.selectedIds = []; this.changePage(1); },
    changePage(value) {
      if (this.auditing) return;
      const page = Number(value);
      if (!Number.isInteger(page) || page < 1 || page > this.pageCount) { this.notify(`请输入 1–${this.pageCount} 之间的页码`); return; }
      this.currentPage = page; this.pageJump = page;
      this.$nextTick(() => { const table = this.$el.querySelector('.table-scroll'); if (table) table.scrollTop = 0; });
    },
    toggleSort() { this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc'; this.changePage(1); },
    canOperateCustomer(row) {
      if (row.canOperate === false) return false;
      // 多机构结果必须明确标记可操作，不能仅凭页面传入的机构归属放行。
      if (this.filters.orgIds && row.canOperate !== true) return false;
      if (row.orgId != null) return String(row.orgId) === String(this.orgId);
      // 兼容旧版单机构响应；显式跨机构查询时缺少权限字段则不允许操作。
      return !this.filters.orgIds;
    },
    isAuditEligible(row) {
      return !this.autoAudit && this.canOperateCustomer(row) && String(row.auditStatus) === '0';
    },
    isApprovalEligible(row) { return this.isAuditEligible(row) && isScreeningPassed(row); },
    toggleSelect(id) {
      if (this.loading || this.auditing || !this.pageRows.some(row => row.customerId === id && this.isAuditEligible(row))) return;
      const index = this.selectedIds.indexOf(id); if (index >= 0) this.selectedIds.splice(index, 1); else this.selectedIds.push(id);
    },
    togglePage() {
      if (this.loading || this.auditing) return;
      const ids = new Set(this.selectablePageRows.map(row => row.customerId));
      this.selectedIds = this.allPageSelected ? this.selectedIds.filter(id => !ids.has(id)) : [...new Set([...this.selectedIds, ...ids])];
    },
    openDrawer(row, tab = 'overview') { this.drawerTab = tab; this.drawerRow = row; },
    focusModal() { this.modalPreviousFocus = document.activeElement; this.$nextTick(() => { if (this.$refs.confirmation) this.$refs.confirmation.focus(); }); },
    closeModal() { if (this.auditing || this.exporting) return; this.showAuditConfirm = false; this.showDeleteConfirm = false; this.deleteTarget = null; this.showExportConfirm = false; if (this.modalPreviousFocus && document.contains(this.modalPreviousFocus)) this.modalPreviousFocus.focus(); },
    trapModalFocus(event) {
      const buttons = this.$refs.confirmation.querySelectorAll('button:not([disabled])');
      if (!buttons.length) { event.preventDefault(); return; }
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.confirmation)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    },
    confirmAudit(row, status) {
      if (this.auditing || !this.isAuditEligible(row)) return;
      if (status === 1 && !this.isApprovalEligible(row)) {
        this.notify(SCREENING_MEDICAL_CONFIRMATION);
        return;
      }
      this.auditTargets = [row.customerId];
      this.auditTargetStatus = status;
      this.showAuditConfirm = true;
      this.focusModal();
    },
    confirmBatchAudit(status) { if (!this.canBatchAudit) return; if (status === 1 && !this.canBatchApprove) { this.notify(SCREENING_MEDICAL_CONFIRMATION); return; } this.auditTargets = this.selectedRows.map(row => row.customerId); this.auditTargetStatus = status; this.showAuditConfirm = true; this.focusModal(); },
    confirmDelete(row) { if (this.auditing || !this.isAuditEligible(row)) return; this.deleteTarget = row; this.showDeleteConfirm = true; this.focusModal(); },
    async doDelete() {
      if (this.auditing || !this.deleteTarget || !this.isAuditEligible(this.deleteTarget)) return;
      this.auditing = true;
      try {
        const res = await deleteSanGaoStaffPendingQuestionnaire(this.deleteTarget.customerId);
        if (!res || res.code !== 200) throw new Error((res && res.msg) || '删除失败，请刷新后重试');
        this.notify('问卷记录已删除，客户可重新填写');
        this.showDeleteConfirm = false;
        this.deleteTarget = null;
        this.selectedIds = [];
        await this.refreshWorkbench();
      } catch (error) { this.notify(error.message || '删除失败，请稍后重试'); }
      finally { this.auditing = false; }
    },
    async doAudit() {
      if (this.auditing || !this.auditTargets.length || this.auditTargets.length > 100) return;
      const targets = this.customerList.filter(row => this.auditTargets.includes(row.customerId)
        && this.isAuditEligible(row));
      if (targets.length !== this.auditTargets.length) return;
      if (this.auditTargetStatus === 1 && targets.some(row => !isScreeningPassed(row))) {
        this.notify(SCREENING_MEDICAL_CONFIRMATION);
        return;
      }
      this.auditing = true;
      try {
        const res = await auditSanGaoStaffCustomer({ customerIds: this.auditTargets.slice(), auditStatus: this.auditTargetStatus });
        if (!res || res.code !== 200) throw new Error((res && res.msg) || '审核失败，请刷新后重试');
        const count = Number(res.data);
        this.notify(Number.isFinite(count) ? `已处理 ${count} 位客户${count < this.auditTargets.length ? '，其余客户状态已变更' : ''}` : '审核已提交');
        this.showAuditConfirm = false; this.selectedIds = []; await this.loadCurrent();
      } catch (error) { this.notify(error.message || '网络异常，请稍后重试'); }
      finally { this.auditing = false; }
    },
    openExport() { if (!this.canExport) return; this.showExportConfirm = true; this.focusModal(); },
    async exportCustomers() {
      if (this.exporting || !this.exportRows.length) return;
      const rows = this.exportRows.slice(); this.exporting = true; await this.$nextTick();
      try {
        downloadCustomerCsv(rows, { includeOrganization: this.organizationFilterEnabled });
        this.showExportConfirm = false;
        this.notify(`已生成 ${rows.length} 位客户的 CSV 文件`);
      }
      catch (e) { this.notify('导出失败，请减少筛选范围后重试'); }
      finally { this.exporting = false; }
    },
    goSalesmanQrcode() { this.$router.push('/sanGao/staff/pc/salesmanQrcode'); },
    goGuide() { if (!this.taikangMode) this.$router.push('/sanGao/staff/pc/guide'); },
    logout() { const query = this.taikangMode ? { taikang: '1' } : {}; localStorage.removeItem(STORAGE_KEYS.token); localStorage.removeItem(STORAGE_KEYS.info); this.$router.replace({ path: '/sanGao/staff/pc/login', query }); },
  },
};
